/**
 * Client-side vitals formatting (aligned with server vitalsService.VitalsToString).
 * Vitals are stored in °F; temperature display respects settings.celcius.
 */

function fahrenheitToCelsius(f) {
  const n = Number(f);
  if (!Number.isFinite(n)) return null;
  return Math.round(((n - 32) * 5 / 9) * 10) / 10;
}

function formatNumericRange(arr, decimals = 0) {
  if (!Array.isArray(arr) || arr.length < 2) return null;
  const a = Number(arr[0]);
  const b = Number(arr[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const fmt = (n) => (decimals > 0 ? n.toFixed(decimals) : String(Math.round(n)));
  return a === b ? fmt(a) : `${fmt(a)}–${fmt(b)}`;
}

export function normalizeSessionVitals(raw) {
  if (raw == null) return null;
  let vitals = raw;
  if (typeof vitals === 'string') {
    try {
      vitals = JSON.parse(vitals);
    } catch {
      return null;
    }
  }
  if (!vitals || typeof vitals !== 'object' || Array.isArray(vitals)) return null;
  const hasHr = Array.isArray(vitals.hr) && vitals.hr.length >= 2;
  const hasBp =
    vitals.bp &&
    typeof vitals.bp === 'object' &&
    Array.isArray(vitals.bp.sys) &&
    Array.isArray(vitals.bp.dia);
  if (!hasHr && !hasBp) return null;
  return vitals;
}

/**
 * @param {object} vitals
 * @param {{ celcius?: boolean }} [options]
 * @returns {{ hr: string, bp: string, spo2: string, rr: string, temp: string, tempUnit: string } | null}
 */
export function formatVitalsForMonitor(vitals, options = {}) {
  const normalized = normalizeSessionVitals(vitals);
  if (!normalized) return null;

  const useCelcius = options.celcius === true;

  const hr = formatNumericRange(normalized.hr) ?? '—';

  let bp = '—';
  const bpObj = normalized.bp;
  if (bpObj && typeof bpObj === 'object') {
    const sys = Array.isArray(bpObj.sys) ? bpObj.sys : [0, 0];
    const dia = Array.isArray(bpObj.dia) ? bpObj.dia : [0, 0];
    if (sys.length >= 2 && dia.length >= 2) {
      bp =
        sys[0] === sys[1] && dia[0] === dia[1]
          ? `${sys[0]}/${dia[0]}`
          : `${sys[0]}/${dia[0]}–${sys[1]}/${dia[1]}`;
    }
  }

  const spo2 = formatNumericRange(normalized.so2) ?? '—';
  const rr = formatNumericRange(normalized.rr) ?? '—';

  const tempArr = Array.isArray(normalized.temp) ? normalized.temp : [0, 0];
  const tempUnit = useCelcius ? '°C' : '°F';
  const t0 = useCelcius ? fahrenheitToCelsius(tempArr[0]) : Number(tempArr[0]);
  const t1 = useCelcius ? fahrenheitToCelsius(tempArr[1]) : Number(tempArr[1]);
  let temp = '—';
  if (Number.isFinite(t0) && Number.isFinite(t1)) {
    const fmt = (n) => (useCelcius ? n.toFixed(1) : String(Math.round(n)));
    temp = t0 === t1 ? fmt(t0) : `${fmt(t0)}–${fmt(t1)}`;
  }

  return { hr, bp, spo2, rr, temp, tempUnit };
}
